<script lang="ts">
    import { VpRefreshIcon } from '@decathlon-experience/icons/svelte';
    import { cva, type VariantProps } from 'class-variance-authority';
    import type { ComponentType } from 'svelte';
    import type { HTMLButtonAttributes } from 'svelte/elements';

    import cn from '$common/utils/cn';

    // Svelte 5 icons from the icon package or legacy Svelte 4 local icons
    type IconComponent = typeof VpRefreshIcon | ComponentType;

    const button = cva('vp-button', {
        variants: {
            intent: {
                primary: 'vp-button--primary',
                secondary: 'vp-button--secondary',
                tertiary: 'vp-button--tertiary',
                negative: 'vp-button--negative',
                inverse: 'vp-button--inverse',
                link: 'bg-none bg-transparent text-content-neutral underline',
            },
            shape: {
                squared: 'vp-button--squared',
                rounded: 'vp-button--rounded',
            },
            size: {
                none: 'p-0',
                small: 'vp-button--small',
                medium: 'vp-button--medium',
                large: 'vp-button--large',
            },
            state: {
                disabled: 'opacity-[0.38] cursor-not-allowed',
                loading: 'animate-pulse',
            },
        },
        compoundVariants: [],
    });

    /**
     * For Svelte components, we recommend setting your defaultVariants within
     * Svelte props (which are `undefined` by default)
     */
    interface $$Props
        extends HTMLButtonAttributes, VariantProps<typeof button> {
        startIcon?: IconComponent;
        endIcon?: IconComponent;
    }

    /**
     * For Svelte components, we recommend setting your defaultVariants within
     * Svelte props (which are `undefined` by default)
     */
    export let intent: $$Props['intent'] = 'primary';
    export let size: $$Props['size'] = 'medium';
    export let shape: $$Props['shape'] = 'squared';
    export let disabled: $$Props['disabled'] = false;
    export let state: $$Props['state'] = undefined;
    export let startIcon: $$Props['startIcon'] = undefined;
    export let endIcon: $$Props['endIcon'] = undefined;
</script>

<button
    {...$$props}
    on:click
    {disabled}
    class={cn(button({ intent, size, shape, state }), $$props.class)}
>
    <span class="vp-button__label">
        {#if state === 'loading'}
            <div class="animate-spin">
                <VpRefreshIcon size={16} />
            </div>
        {:else}
            {#if startIcon}
                <span data-part="start-slot">
                    <svelte:component this={startIcon} />
                </span>
            {/if}
            <slot />
            {#if endIcon}
                <span data-part="end-slot">
                    <svelte:component this={endIcon} />
                </span>
            {/if}
        {/if}
    </span>
</button>
